import React, { useCallback, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { RichTextEditor } from "../../../../components/RichTextEditor";
import { TextInput } from "../../../../components/TextInput";
import { EditNoteIcon, CheckIcon, CrossIcon } from "../../../../components/Icons";
import { colors } from "../../../../theme/colors";
import { useInlineEditor } from "../../../../hooks/useInlineEditor";
import {
  SectionContainer,
  TitleRow,
  SectionTitle,
  SectionContent,
  EditIconButton,
  SaveIconButton,
  CancelIconButton,
  EditorActions,
} from "./TreatmentPlanEditableSection.styled";

type EditorType = "richText" | "multilineText";

interface TreatmentPlanEditableSectionProps {
  title: string;
  content: string;
  isEditable?: boolean;
  editorType: EditorType;
  placeholder?: string;
  allowHtml?: boolean;
  onSave: (content: string) => Promise<void>;
  onEditingChange?: (isEditing: boolean) => void;
}

const TreatmentPlanEditableSection: React.FC<
  TreatmentPlanEditableSectionProps
> = ({
  title,
  content,
  isEditable = false,
  editorType,
  placeholder,
  allowHtml = false,
  onSave,
  onEditingChange,
}) => {
  const { t } = useTranslation();
  const handleSave = useCallback(
    async (draftContent: string) => {
      await onSave(draftContent);
    },
    [onSave],
  );
  const {
    isEditing,
    draft: draftContent,
    setDraft: setDraftContent,
    saving,
    startEdit,
    cancelEdit,
    save,
  } = useInlineEditor(content, handleSave);

  useEffect(() => {
    onEditingChange?.(isEditing);

    return () => {
      onEditingChange?.(false);
    };
  }, [isEditing, onEditingChange]);

  const renderEditor = () => {
    if (editorType === "richText") {
      return (
        <RichTextEditor
          value={draftContent}
          onChange={setDraftContent}
          placeholder={placeholder}
          minHeight={100}
        />
      );
    }

    return (
      <TextInput
        name={`${title}-editor`}
        placeholder={placeholder}
        value={draftContent}
        onChange={(event) => setDraftContent(event.target.value)}
        multiline
        height="100px"
        rows={10}
      />
    );
  };

  return (
    <SectionContainer>
      <TitleRow>
        <SectionTitle>{title}</SectionTitle>
        {isEditable &&
          (isEditing ? (
            <EditorActions>
              <SaveIconButton
                onClick={save}
                disabled={saving}
                aria-label={t("treatmentPlan.saveChanges")}
              >
                {saving ? (
                  <CircularProgress size={20} />
                ) : (
                  <CheckIcon
                    width="24px"
                    height="24px"
                    fill={colors.primary.main}
                  />
                )}
              </SaveIconButton>
              <CancelIconButton
                onClick={cancelEdit}
                disabled={saving}
                aria-label={t("treatmentPlan.cancel")}
              >
                <CrossIcon
                  width="24px"
                  height="24px"
                  fill={colors.status.error}
                />
              </CancelIconButton>
            </EditorActions>
          ) : (
            <EditIconButton
              onClick={startEdit}
              aria-label={t("treatmentPlan.editTreatmentPlan")}
            >
              <EditNoteIcon width="20px" height="20px" />
            </EditIconButton>
          ))}
      </TitleRow>

      {isEditing
        ? renderEditor()
        : allowHtml
          ? (
            <SectionContent
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )
          : (
            <SectionContent>{content}</SectionContent>
          )}
    </SectionContainer>
  );
};

export default TreatmentPlanEditableSection;
